'use client';

import type React from 'react';
import { InputText } from 'src/components/InputText';
import { Checkbox } from 'src/components/Checkbox';
import type { Product } from 'src/model/Product';
import { Package, Truck, Shield, Ruler } from 'lucide-react';

interface WarrantyShippingDimensionsProps {
  formData: Product;
  formErrors: any;
  onChangeFormInput: (inputName: string) => (value: string) => void;
  onShippingCalc: (
    height: string,
    width: string,
    depth: string,
    weight: string
  ) => void;
  shippingCost: string | null;
}

const dimensionFields = [
  { key: 'width', label: 'Largura' },
  { key: 'height', label: 'Altura' },
  { key: 'depth', label: 'Profundidade' },
  { key: 'weight', label: 'Peso' },
] as const;

const labelsMap: Record<string, string> = {
  width: 'Largura',
  height: 'Altura',
  depth: 'Profundidade',
  weight: 'Peso',
};

export const WarrantyShippingDimensions: React.FC<
  WarrantyShippingDimensionsProps
> = ({
  formData,
  formErrors,
  onChangeFormInput,
  onShippingCalc,
  shippingCost,
}) => {
  const toBool = (v: unknown): boolean =>
    v === true || v === 'true' || v === 1 || v === '1';
  const isFreeShipping = toBool(formData.freeShipping);

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-100 to-red-50 border border-red-200">
            <Truck className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
            Garantia e Frete
          </h2>
        </div>
        <p className="text-muted-foreground text-base ml-[60px]">
          Configure garantia, frete e dimensões do produto
        </p>
      </div>

      <div className="bg-card rounded-2xl border-2 border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="p-8 space-y-8">
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-100">
                <Shield className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                Garantia
              </h3>
            </div>
            <div className="max-w-md">
              <InputText
                name="warrantyValue"
                label="Período de garantia *"
                placeholder="Ex: 12 meses, 1 ano..."
                onChange={(e) =>
                  onChangeFormInput('warrantyValue')(e.target.value)
                }
                error={formErrors.warranty?.[0]}
                inputClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all duration-300 outline-none"
              />
              {formData.warranty?.warranty && (
                <div className="mt-3 px-3 py-2 bg-muted/50 rounded-lg border border-border">
                  <p className="text-xs text-muted-foreground">
                    Valor atual:{' '}
                    <span className="font-semibold text-foreground">
                      {formData.warranty.warranty}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-100">
                <Truck className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Frete</h3>
            </div>

            {shippingCost && (
              <div className="bg-gradient-to-br from-red-700/5 to-red-700/10 rounded-xl p-5 border border-red-700/20">
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  A taxa de frete abaixo é um valor padrão. O valor exato será
                  calculado no momento da compra com base no endereço.
                </p>
                <div className="flex justify-between items-center p-4 bg-background/80 backdrop-blur-sm rounded-lg border border-border">
                  <span className="text-sm font-medium text-foreground">
                    Frete Base:
                  </span>
                  <span className="text-red-700 font-bold text-xl">
                    {shippingCost}
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 p-5 bg-muted/30 rounded-xl border border-border hover:border-red-700/30 transition-all cursor-pointer">
              <Checkbox
                name="freeShipping"
                value="true"
                checked={isFreeShipping}
                label="Oferecer frete grátis"
                onChange={(value) =>
                  onChangeFormInput('freeShipping')(
                    value === 'true' ? 'true' : 'false'
                  )
                }
                checkboxClassName="w-5 h-5 border-red-500 border-2 accent-red-600 cursor-pointer"
                labelClassName="text-foreground text-sm font-semibold cursor-pointer"
                error={formErrors.freeShipping?.[0]}
              />
              {isFreeShipping && (
                <span className="ml-auto px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                  Ativo
                </span>
              )}
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-red-100">
                <Package className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                Dimensões do Produto
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {dimensionFields.map(({ key, label }) => {
                const cap = key[0].toUpperCase() + key.slice(1);
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Ruler className="w-4 h-4 text-red-600" />
                      <span className="text-xs font-medium text-muted-foreground uppercase">
                        {label}
                      </span>
                    </div>
                    <InputText
                      name={`dimensions${key}`}
                      type="number"
                      label={`${label} *`}
                      placeholder="0"
                      onChange={(e) =>
                        onChangeFormInput(`dimensions${cap}`)(e.target.value)
                      }
                      onBlur={() =>
                        onShippingCalc(
                          formData.dimensions.height,
                          formData.dimensions.width,
                          formData.dimensions.depth,
                          formData.dimensions.weight
                        )
                      }
                      error={formErrors[`dimensions${cap}`]?.[0]}
                      inputClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all duration-300 outline-none font-semibold"
                    />
                  </div>
                );
              })}
            </div>

            {(formData.dimensions.width ||
              formData.dimensions.height ||
              formData.dimensions.depth ||
              formData.dimensions.weight) && (
              <div className="mt-5 p-4 bg-muted/30 rounded-lg border border-border">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Dimensões atuais:
                </p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(formData.dimensions).map(([k, v]) =>
                    v ? (
                      <span
                        key={k}
                        className="text-xs px-3 py-1.5 bg-background rounded-md border border-border"
                      >
                        <span className="text-muted-foreground">
                          {labelsMap[k] ?? k}:
                        </span>{' '}
                        <span className="font-semibold text-foreground">
                          {v}
                        </span>
                      </span>
                    ) : null
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
